/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface OptionalLazyErrorBoundaryProps {
    children: ReactNode;
    fallback?: ReactNode;
    /** Clear a contained error when the caller starts a new lazy-load attempt. */
    resetKey?: unknown;
}

interface OptionalLazyErrorBoundaryState {
    hasError: boolean;
}

/**
 * Contains failures from optional lazy UI so a rejected chunk cannot replace the whole page.
 * The default fallback is intentionally empty; callers may provide a stable, non-interactive
 * placeholder when the failed component owns visible layout.
 */
export class OptionalLazyErrorBoundary extends Component<
    OptionalLazyErrorBoundaryProps,
    OptionalLazyErrorBoundaryState
> {
    state: OptionalLazyErrorBoundaryState = { hasError: false };

    static getDerivedStateFromError(): OptionalLazyErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(_error: Error, _errorInfo: ErrorInfo): void {
        // Customer integration point: report optional chunk failures to the configured browser APM.
    }

    componentDidUpdate(previousProps: OptionalLazyErrorBoundaryProps): void {
        if (this.state.hasError && previousProps.resetKey !== this.props.resetKey) {
            this.setState({ hasError: false });
        }
    }

    render(): ReactNode {
        return this.state.hasError ? (this.props.fallback ?? null) : this.props.children;
    }
}
